// Tenantra Inc. - Add Tenancy Page (Microsoft 365 Style - Compact)
import { useState, useEffect } from 'react';
import { 
  ArrowLeft, Users, Home, Calendar, DollarSign, CheckCircle,
  AlertTriangle, Loader2, ChevronDown, Search, UserPlus, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import api, { apiHelpers } from '@/services/api';
import { track } from '@/services/analytics';

const RENT_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'weekly', label: 'Weekly' }
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function DateDropdowns({ value, onChange, error, startYear, endYear, testIdPrefix }) {
  const initParts = value ? value.split('-') : ['', '', ''];
  const [selYear, setSelYear] = useState(initParts[0] || '');
  const [selMonth, setSelMonth] = useState(initParts[1] ? String(parseInt(initParts[1])) : '');
  const [selDay, setSelDay] = useState(initParts[2] ? String(parseInt(initParts[2])) : '');

  const doUpdate = (y, m, d) => {
    if (y && m && d) {
      onChange(`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`);
    }
  };

  const handleMonth = (v) => { setSelMonth(v); doUpdate(selYear, v, selDay); };
  const handleDay = (v) => { setSelDay(v); doUpdate(selYear, selMonth, v); };
  const handleYear = (v) => { setSelYear(v); doUpdate(v, selMonth, selDay); };

  const sY = startYear || 2000;
  const eY = endYear || new Date().getFullYear() + 5;
  const years = [];
  for (let i = eY; i >= sY; i--) years.push(i);

  const days = [];
  const maxDay = selMonth && selYear ? new Date(parseInt(selYear), parseInt(selMonth), 0).getDate() : 31;
  for (let i = 1; i <= maxDay; i++) days.push(i);

  const sel = "h-8 px-2 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none";

  return (
    <div className="flex gap-1.5">
      <div className="relative flex-1">
        <select value={selMonth} onChange={(e) => handleMonth(e.target.value)}
          className={`w-full ${sel} ${error ? 'border-red-400' : ''}`} data-testid={`${testIdPrefix}-month`}>
          <option value="">Month</option>
          {MONTHS.map((m, i) => <option key={i} value={String(i+1)}>{m}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
      <div className="relative w-[70px]">
        <select value={selDay} onChange={(e) => handleDay(e.target.value)}
          className={`w-full ${sel} ${error ? 'border-red-400' : ''}`} data-testid={`${testIdPrefix}-day`}>
          <option value="">Day</option>
          {days.map(d => <option key={d} value={String(d)}>{d}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
      <div className="relative w-[85px]">
        <select value={selYear} onChange={(e) => handleYear(e.target.value)}
          className={`w-full ${sel} ${error ? 'border-red-400' : ''}`} data-testid={`${testIdPrefix}-year`}>
          <option value="">Year</option>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>
    </div>
  );
}

export default function AddTenancyPage() {
  const [loading, setLoading] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState(null);
  const [unitId, setUnitId] = useState('');
  const [errors, setErrors] = useState({});
  
  // Tenant lookup state
  const [tenantMode, setTenantMode] = useState(null);
  const [lookupId, setLookupId] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [foundTenant, setFoundTenant] = useState(null);
  
  const [formData, setFormData] = useState({
    property_id: new URLSearchParams(window.location.search).get('property') || '',
    tenant_name: '',
    tenant_dob: '',
    tenant_email: '',
    tenant_phone: '',
    move_in_date: '',
    move_out_date: '',
    rent_amount: '',
    rent_frequency: 'monthly',
    consent_confirmed: false
  });
  useEffect(() => {
    setUnits(null); setUnitId('');
    if (!formData.property_id) return;
    api.get(`/properties/${formData.property_id}/units`).then(r => { const v = r.data.items.filter(u => u.status === 'vacant'); setUnits(v); if (v.length === 1) setUnitId(v[0].id); }).catch(() => setUnits([]));
  }, [formData.property_id]); // eslint-disable-line

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties?status=verified&limit=500');
      setProperties(response.data.items || []);
    } catch (err) {
      toast.error('Failed to load properties');
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleTenantLookup = async () => {
    if (!lookupId || lookupId.length < 6) {
      toast.error('Please enter a valid 6-digit Tenant ID');
      return;
    }
    
    try {
      setLookingUp(true);
      const response = await api.post('/tenants/lookup-by-id', {
        tenant_unique_id: lookupId
      });
      setFoundTenant(response.data);
      toast.success('Tenant record found');
    } catch (err) {
      toast.error('Tenant not found with this ID');
      setFoundTenant(null);
    } finally {
      setLookingUp(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.property_id && units && units.length !== 1 && !unitId) newErrors.unit_id = units.length ? 'Choose a vacant unit' : 'No vacant units at this property';
    if (!formData.property_id) {
      newErrors.property_id = 'Please select a property';
    }
    
    if (tenantMode === 'new') {
      if (!formData.tenant_name || formData.tenant_name.length < 2) {
        newErrors.tenant_name = 'Tenant name is required';
      }
      if (!formData.tenant_dob) {
        newErrors.tenant_dob = 'Date of birth is required';
      }
    } else if (tenantMode === 'lookup') {
      if (!foundTenant) {
        newErrors.tenant_lookup = 'Please look up a valid tenant first';
      }
    } else {
      newErrors.tenant_mode = 'Please select how to add the tenant';
    }
    
    if (!formData.move_in_date) {
      newErrors.move_in_date = 'Move-in date is required';
    }
    if (!formData.rent_amount || parseFloat(formData.rent_amount) <= 0) {
      newErrors.rent_amount = 'Valid rent amount is required';
    }
    if (!formData.consent_confirmed) {
      newErrors.consent_confirmed = 'You must confirm you will obtain tenant consent';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setLoading(true);
      
      if (tenantMode === 'lookup' && foundTenant) {
        await api.post('/tenancies/by-tenant-id', {
          property_id: formData.property_id,
          unit_id: unitId || null,
          tenant_unique_id: foundTenant.tenant_unique_id,
          move_in_date: formData.move_in_date,
          move_out_date: formData.move_out_date || null,
          rent_amount: parseFloat(formData.rent_amount),
          rent_frequency: formData.rent_frequency
        });
      } else {
        await api.post('/tenancies', {
          property_id: formData.property_id,
          unit_id: unitId || null,
          tenant_name: formData.tenant_name,
          tenant_dob: formData.tenant_dob,
          tenant_email: formData.tenant_email || null,
          tenant_phone: formData.tenant_phone || null,
          move_in_date: formData.move_in_date,
          move_out_date: formData.move_out_date || null,
          rent_amount: parseFloat(formData.rent_amount),
          rent_frequency: formData.rent_frequency,
          consent_confirmed: formData.consent_confirmed
        });
      }
      
      track('tenancy_created');
      toast.success(formData.tenant_email || (tenantMode === 'lookup' && foundTenant?.email) ? 'Tenancy created. Consent email sent to the tenant.' : 'Tenancy created.');
      navigate('/landlord/tenants');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header - Compact */}
      <div>
        <a 
          href="/landlord/tenants" 
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Tenants
        </a>
        <h1 className="text-lg font-semibold text-slate-800">Add New Tenancy</h1>
      </div>

      {/* No verified properties warning */}
      {!loadingProperties && properties.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">No Verified Properties</p>
              <p className="text-xs text-amber-700 mt-0.5">
                You need at least one verified property before you can add tenancies.
              </p>
              <a href="/landlord/properties" className="text-xs text-teal-600 hover:text-teal-700 font-medium mt-1 inline-block">
                Go to Properties →
              </a>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Property Selection - Compact */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Home className="w-4 h-4 text-teal-500" />
            <span className="text-sm font-medium text-slate-700">Select Property</span>
          </div>
          
          {loadingProperties ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="relative w-10 h-10 mx-auto mb-3">
                  <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-teal-500 animate-spin"></div>
                </div>
                <p className="text-slate-600 text-sm">Loading properties...</p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <select
                value={formData.property_id}
                onChange={(e) => handleChange('property_id', e.target.value)}
                data-testid="tenancy-property-select"
                className={`w-full h-9 px-3 pr-8 text-sm bg-slate-50 border rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 appearance-none ${
                  errors.property_id ? 'border-red-400' : 'border-slate-200'
                }`}
              >
                <option value="">Choose a property...</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.address.street}{p.address.unit_number && (p.units?.length || 1) <= 1 ? ` #${p.address.unit_number}` : ''} - {p.address.city}{p.units?.length > 1 ? ` (${p.units.length} units)` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}
          {formData.property_id && units && (
            <div className="mt-3" data-testid="tenancy-unit-field">
              <label className="block text-xs text-slate-600 mb-1">Unit</label>
              {units.length === 0 ? <p className="text-xs text-amber-700" data-testid="tenancy-no-vacant">No vacant units at this property.</p> : (
                <div className="relative"><select value={unitId} onChange={(e) => { setUnitId(e.target.value); setErrors(p => ({ ...p, unit_id: null })); }} className="w-full h-9 px-3 pr-8 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-800 appearance-none focus:outline-none focus:ring-1 focus:ring-teal-500" data-testid="tenancy-unit-select">
                  {units.length > 1 && <option value="">Choose a vacant unit...</option>}
                  {units.map(u => <option key={u.id} value={u.id}>Unit {u.number}</option>)}
                </select><ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" /></div>
              )}
              {errors.unit_id && <p className="text-red-500 text-xs mt-1.5">{errors.unit_id}</p>}
            </div>
          )}
          {errors.property_id && (
            <p className="text-red-500 text-xs mt-1.5">{errors.property_id}</p>
          )}
        </div>

        {/* Tenant Selection - Compact Pills */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-teal-500" />
            <span className="text-sm font-medium text-slate-700">Add Tenant</span>
          </div>
          
          {/* Mode Selection Pills */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => { setTenantMode('lookup'); setFoundTenant(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                tenantMode === 'lookup'
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <Search className={`w-3.5 h-3.5 ${tenantMode === 'lookup' ? 'text-teal-600' : 'text-slate-400'}`} />
              Enter Tenant ID
            </button>
            
            <button
              type="button"
              data-testid="tenancy-mode-new"
              onClick={() => { setTenantMode('new'); setFoundTenant(null); setLookupId(''); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                tenantMode === 'new'
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <UserPlus className={`w-3.5 h-3.5 ${tenantMode === 'new' ? 'text-teal-600' : 'text-slate-400'}`} />
              New Tenant
            </button>
          </div>
          {errors.tenant_mode && (
            <p className="text-red-500 text-xs mb-3">{errors.tenant_mode}</p>
          )}
          
          {/* Lookup Mode */}
          {tenantMode === 'lookup' && (
            <div className="space-y-3 pt-3 border-t border-slate-200">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Tenantra Tenant ID</Label>
                <div className="flex gap-2">
                  <Input
                    value={lookupId}
                    onChange={(e) => setLookupId(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 h-8 text-sm bg-slate-50 border-slate-200 font-mono tracking-wider"
                    maxLength={6}
                  />
                  <Button
                    type="button"
                    onClick={handleTenantLookup}
                    disabled={lookingUp || lookupId.length < 6}
                    className="h-8 px-3 text-sm bg-teal-600 hover:bg-teal-700"
                  >
                    {lookingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
              
              {foundTenant && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{foundTenant.full_name}</p>
                      <p className="text-xs text-slate-600">
                        DOB: {foundTenant.date_of_birth?.split('T')[0]}
                        {foundTenant.email_masked && ` • ${foundTenant.email_masked}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {errors.tenant_lookup && (
                <p className="text-red-500 text-xs">{errors.tenant_lookup}</p>
              )}
            </div>
          )}
          
          {/* New Tenant Mode */}
          {tenantMode === 'new' && (
            <div className="space-y-3 pt-3 border-t border-slate-200">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Full Legal Name *</Label>
                  <Input
                    value={formData.tenant_name}
                    onChange={(e) => handleChange('tenant_name', e.target.value)}
                    data-testid="tenancy-tenant-name"
                    className={`h-8 text-sm bg-slate-50 border-slate-200 ${errors.tenant_name ? 'border-red-400' : ''}`}
                  />
                  {errors.tenant_name && <p className="text-red-500 text-xs mt-1">{errors.tenant_name}</p>}
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Date of Birth *</Label>
                  <DateDropdowns
                    value={formData.tenant_dob}
                    onChange={(v) => handleChange('tenant_dob', v)}
                    error={errors.tenant_dob}
                    startYear={1920}
                    endYear={new Date().getFullYear() - 16}
                    testIdPrefix="tenant-dob"
                  />
                  {errors.tenant_dob && <p className="text-red-500 text-xs mt-1">{errors.tenant_dob}</p>}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Email (for consent)</Label>
                  <Input
                    type="email"
                    value={formData.tenant_email}
                    onChange={(e) => handleChange('tenant_email', e.target.value)}
                    className="h-8 text-sm bg-slate-50 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Phone (optional)</Label>
                  <Input
                    type="tel"
                    value={formData.tenant_phone}
                    onChange={(e) => handleChange('tenant_phone', e.target.value)}
                    className="h-8 text-sm bg-slate-50 border-slate-200"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lease Details - Compact */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-teal-500" />
            <span className="text-sm font-medium text-slate-700">Lease Details</span>
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Move-in Date *</Label>
                <DateDropdowns
                  value={formData.move_in_date}
                  onChange={(v) => handleChange('move_in_date', v)}
                  error={errors.move_in_date}
                  startYear={2020}
                  endYear={new Date().getFullYear() + 2}
                  testIdPrefix="move-in"
                />
                {errors.move_in_date && <p className="text-red-500 text-xs mt-1">{errors.move_in_date}</p>}
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Move-out Date (if ended)</Label>
                <DateDropdowns
                  value={formData.move_out_date}
                  onChange={(v) => handleChange('move_out_date', v)}
                  startYear={2020}
                  endYear={new Date().getFullYear() + 2}
                  testIdPrefix="move-out"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Rent Amount ($CAD) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.rent_amount}
                    onChange={(e) => handleChange('rent_amount', e.target.value)}
                    data-testid="tenancy-rent-amount"
                    className={`h-8 pl-8 text-sm bg-slate-50 border-slate-200 ${errors.rent_amount ? 'border-red-400' : ''}`}
                  />
                </div>
                {errors.rent_amount && <p className="text-red-500 text-xs mt-1">{errors.rent_amount}</p>}
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Payment Frequency</Label>
                <div className="relative">
                  <select
                    value={formData.rent_frequency}
                    onChange={(e) => handleChange('rent_frequency', e.target.value)}
                    className="w-full h-8 px-2.5 pr-8 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none"
                  >
                    {RENT_FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consent Confirmation - Compact */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
          <div className="flex items-start gap-2.5">
            <Checkbox
              data-testid="tenancy-consent-checkbox"
              id="consent"
              checked={formData.consent_confirmed}
              onCheckedChange={(checked) => handleChange('consent_confirmed', checked)}
              className="mt-0.5"
            />
            <div>
              <Label htmlFor="consent" className="text-sm text-slate-800 font-medium cursor-pointer">
                I will obtain tenant consent *
              </Label>
              <p className="text-xs text-slate-600 mt-0.5">
                A consent email will be sent to the tenant. Proceeding without proper consent violates Terms of Service and PIPEDA.
              </p>
              {errors.consent_confirmed && (
                <p className="text-red-500 text-xs mt-1">{errors.consent_confirmed}</p>
              )}
            </div>
          </div>
        </div>

        {/* Submit - Compact */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <a href="/landlord/tenants">
            <Button type="button" variant="outline" className="h-8 px-3 text-sm border-slate-200 text-slate-600">
              Cancel
            </Button>
          </a>
          <Button 
            type="submit" 
            data-testid="tenancy-submit"
            disabled={loading || properties.length === 0}
            className="h-8 px-4 text-sm bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                Create Tenancy
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
